import { useQuery } from '@tanstack/react-query';
import { Loading, EmptyState, Badge } from '@/components/ui';
import { formatNIS } from '@/utils/currency';
import { formatDate } from '@/utils/date';
import { affiliateApi, type PartnerPayout, type PayoutStatus } from '@/api/affiliate';

const PAYOUT_VARIANT: Record<PayoutStatus, 'success' | 'warning' | 'info' | 'neutral'> = {
  pending: 'warning',
  held: 'neutral',
  requested: 'info',
  approved: 'info',
  paid: 'success',
  invoiced: 'success',
};

function customerPaidBase(payout: PartnerPayout): {
  net: number | null;
  vat: number | null;
  gross: number;
} {
  const gross = payout.gross_customer_paid_agorot;
  const net = payout.net_customer_paid_agorot;
  if (net == null) return { net: null, vat: null, gross };
  return { net, vat: gross - net, gross };
}

function CustomerPaidCell({ payout }: { payout: PartnerPayout }) {
  const { net, vat, gross } = customerPaidBase(payout);
  return (
    <div className="flex flex-col gap-0.5 num leading-tight" dir="ltr">
      <span className="text-white font-medium">
        {net == null ? '—' : formatNIS(net)}
        <span className="ml-1 text-[11px] font-normal text-ink-muted">net</span>
      </span>
      <span className="text-[11px] text-ink-secondary">
        VAT {vat == null ? '—' : formatNIS(vat)}
      </span>
      <span className="text-[11px] text-ink-secondary">gross {formatNIS(gross)}</span>
    </div>
  );
}

export default function EarningsTab({ partnerId }: { partnerId: string }) {
  const { data: txns, isLoading: loadingTxns } = useQuery({
    queryKey: ['affiliate-credit-txns', partnerId],
    queryFn: () => affiliateApi.earnings.creditTransactionsByPartner(partnerId),
  });
  const { data: payouts, isLoading: loadingPayouts } = useQuery({
    queryKey: ['affiliate-partner-payouts', partnerId],
    queryFn: () => affiliateApi.payouts.listByPartner(partnerId),
  });

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <div>
          <h3 className="text-sm font-semibold text-white">Payout history</h3>
          <p className="text-xs text-ink-muted mt-0.5">
            Commission % is calculated on net (before VAT).
          </p>
        </div>
        {loadingPayouts ? (
          <Loading text="Loading payouts…" />
        ) : !(payouts ?? []).length ? (
          <EmptyState title="No payouts yet" description="Monthly payout roll-ups will appear here once settled." />
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-muted text-xs uppercase">
                  <th className="px-4 py-3">Period</th>
                  <th className="px-4 py-3">Net / VAT / Gross</th>
                  <th className="px-4 py-3">%</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Active subs</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {payouts!.map((p) => (
                  <tr key={p.id} className="border-t border-navy-border">
                    <td className="px-4 py-3">{formatDate(p.period)}</td>
                    <td className="px-4 py-3">
                      <CustomerPaidCell payout={p} />
                    </td>
                    <td className="px-4 py-3 num">{p.commission_pct}%</td>
                    <td className="px-4 py-3 num">{formatNIS(p.amount_agorot)}</td>
                    <td className="px-4 py-3 num">{p.active_subs_count}</td>
                    <td className="px-4 py-3">
                      <Badge variant={PAYOUT_VARIANT[p.status]}>{p.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {p.invoice_number
                        ? `${p.invoice_number}${p.invoice_received_at ? ` (received ${formatDate(p.invoice_received_at)})` : ''}`
                        : p.invoice_requested_at
                          ? `requested ${formatDate(p.invoice_requested_at)}`
                          : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-white">Accrual ledger</h3>
        {loadingTxns ? (
          <Loading text="Loading ledger…" />
        ) : !(txns ?? []).length ? (
          <EmptyState title="No accruals yet" description="Per-referral commission accruals will appear here." />
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-muted text-xs uppercase">
                  <th className="px-4 py-3">Period</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Description</th>
                </tr>
              </thead>
              <tbody>
                {txns!.map((t) => (
                  <tr key={t.id} className="border-t border-navy-border">
                    <td className="px-4 py-3">{t.period ? formatDate(t.period) : '—'}</td>
                    <td className="px-4 py-3 capitalize">{t.type.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3 num">{formatNIS(t.amount_agorot)}</td>
                    <td className="px-4 py-3 text-ink-secondary">{t.description ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
